import React, { useState, useEffect } from 'react'
import { CFormTextarea, CFormLabel, CBadge, CNav, CNavItem, CNavLink, CTabContent, CTabPane, CSpinner } from '@coreui/react'
import { Pencil, Trash2 } from 'lucide-react'
import SuggestionCopy from './SuggestionCopy'

const PlanStatement = ({
  keywords,
  setKeywords,
  handleSubmitWhenTabChange,
  label = 'Keywords',
  editMode,
  setEditMode,
  parentId,
  setParentAccordions,
  setSuggestionModalVisible,
  type,
  differentModuleCopyFunctionality,
  ApiCallingType,
  order,
  setOrder,
  handleSuggestionsChange,
  planSuggestions,
  setPlanSuggestions,
  handlePlanSuggestionsChange,
}) => {
  const [textInput, setTextInput] = useState('')
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [editIndex, setEditIndex] = useState(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (Array.isArray(keywords) && keywords.length > 0) {
      if (typeof keywords[0] === 'string') {
        const formattedKeywords = keywords.map((item) => ({
          name: item,
          description: '',
        }))
        setKeywords(formattedKeywords)
      }
    }
  }, [keywords, setKeywords])

  const handleInputChange = (event) => {
    setTextInput(event.target.value)
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && textInput.trim()) {
      event.preventDefault()
      const newKeyword = textInput.trim()

      if (editIndex !== null) {
        const oldName = keywords[editIndex].name
        if (order && order.length > 0) {
          handleOrderChanges(oldName, newKeyword)
        }
        if (planSuggestions && planSuggestions.length > 0) {
          handlePlanChanges(oldName, newKeyword)
        }
        const updatedKeywords = [...keywords]
        updatedKeywords[editIndex] = {
          ...updatedKeywords[editIndex],
          name: newKeyword,
        }
        setKeywords(updatedKeywords)
        setEditIndex(null)
      } else {
        const newKeywordObject = { name: newKeyword, description: '' }
        setKeywords([...keywords, newKeywordObject])
      }

      setTextInput('')
      if (editMode) setEditMode(false)
    }
  }

  const handleOrderChanges = (oldName, newName) => {
    let updatedOrders
    setOrder((prevOrders) => {
      updatedOrders = prevOrders.map((order) => (order.name === oldName ? { ...order, name: newName } : order))
      return updatedOrders
    })
    handleSuggestionsChange(updatedOrders, parentId)
    // setOrder([])
  }

  const handlePlanChanges = (oldName, newName) => {
    setPlanSuggestions((prevPlanSuggestions) => {
      const updatedPlanSuggestions = prevPlanSuggestions.map((suggestion) => (suggestion.name === oldName ? { ...suggestion, name: newName } : suggestion))
      handlePlanSuggestionsChange(updatedPlanSuggestions, parentId)
      return updatedPlanSuggestions
    })
  }

  const handleEditKeyword = (index) => {
    const selectedKeyword = keywords[index]
    setTextInput(selectedKeyword.name)
    // setKeywords(keywords.filter((_, i) => i !== index));
    setEditIndex(index)
    setEditMode?.(true)
  }

  const handleRemoveKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index))
  }

  const handleDragStart = (index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (event, index) => {
    event.preventDefault()
    if (index === draggedIndex) return

    const reordered = Array.from(keywords)
    const [draggedItem] = reordered.splice(draggedIndex, 1)
    reordered.splice(index, 0, draggedItem)
    setKeywords(reordered)
    setDraggedIndex(index)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const handleTabChange = (tabIndex) => {
    setLoading(true)
    setActiveTab(tabIndex)
    setTimeout(() => {
      setLoading(false)
    }, 1000)
  }
  return (
    <div className="">
      <div className="pe-tab-underline-nav mb-4">
        <CNav role="tablist" className="justify-content-center">
          <CNavItem>
            <CNavLink active={activeTab === 0} onClick={() => setActiveTab(0)}>
              New Statements
            </CNavLink>
          </CNavItem>
          <CNavItem>
            <CNavLink
              active={activeTab === 1}
              onClick={() => {
                setActiveTab(1)
                handleSubmitWhenTabChange?.(false, false)
              }}
            >
              Copy Statements
            </CNavLink>
          </CNavItem>
        </CNav>
      </div>
      <div>
        <div activeTab={activeTab} onActiveTabChange={setActiveTab}>
          <CTabContent className="mt-3">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <CSpinner color="info" />
              </div>
            ) : (
              <>
                <CTabPane visible={activeTab === 0}>
                  <div>
                    <CFormLabel>{label}</CFormLabel>
                    <CFormTextarea
                      type="text"
                      placeholder={'Type and press enter to add ' + label}
                      value={textInput}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      name="assessment"
                      rows={1}
                    />
                    <div
                      className="statement-entry-list"
                      style={{
                        marginTop: '10px',
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      {keywords?.map((keyword, index) => (
                        <CBadge
                          key={index}
                          color="primary"
                          className="statement-entry-pill"
                          draggable // Make the badge draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(event) => handleDragOver(event, index)}
                          onDragEnd={handleDragEnd}
                          style={{
                            cursor: 'grab',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            width: 'fit-content',
                          }}
                        >
                          <span>{keyword.name || keyword}</span>
                          <span className="statement-entry-pill__actions">
                            <Pencil
                              size={12}
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => handleEditKeyword(index)}
                              className="text-white"
                            />
                            <Trash2
                              className="text-white"
                              size={12}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleRemoveKeyword(index)}
                            />
                          </span>
                        </CBadge>
                      ))}
                    </div>
                  </div>
                </CTabPane>
                <CTabPane visible={activeTab === 1}>
                  <SuggestionCopy setParentAccordions={setParentAccordions} ApiCallingType={ApiCallingType} parentId={parentId} setSuggestionModalVisible={setSuggestionModalVisible} type={type} differentModuleCopyFunctionality={differentModuleCopyFunctionality} />
                </CTabPane>
              </>
            )}
          </CTabContent>
        </div>
      </div>
    </div>
  )
}

export default PlanStatement
